import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AuthLayout, AuthAlert } from './AuthLayout';
import { Input, Label, FieldError } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useLocaleStore } from '@/stores/locale';
import { requestPasswordReset } from './api';
import { authErrorMessage } from '@/lib/api-errors';

function buildSchema(t: TFunction<'auth'>) {
  return z.object({ email: z.string().trim().toLowerCase().email(t('validation.emailInvalid')) });
}
type FormValues = { email: string };

export function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const locale = useLocaleStore((s) => s.locale);
  const navigate = useNavigate();
  const [authError, setAuthError] = useState<string | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => buildSchema(t), [locale]);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  async function onSubmit(values: FormValues) {
    setAuthError(null);
    try {
      await requestPasswordReset(values.email);
      navigate(`/reset-password?email=${encodeURIComponent(values.email)}`);
    } catch (err) {
      setAuthError(authErrorMessage(err, t('forgotPassword.sendError')));
    }
  }

  return (
    <AuthLayout title={t('forgotPassword.title')} subtitle={t('forgotPassword.subtitle')}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        {authError ? <AuthAlert>{authError}</AuthAlert> : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">{t('forgotPassword.emailLabel')}</Label>
          <Input id="email" type="email" autoComplete="email" {...register('email')} />
          <FieldError>{errors.email?.message}</FieldError>
        </div>
        <Button type="submit" loading={isSubmitting} className="mt-2 w-full">
          {t('forgotPassword.submit')}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-muted">
        <Link to="/login" className="font-semibold text-accent-ink hover:underline">
          {t('forgotPassword.backToSignIn')}
        </Link>
      </p>
    </AuthLayout>
  );
}
